import React, { createContext, useContext, ReactNode, useMemo } from "react";
import { useUseCasesData, UseCaseData } from "@/hooks/useUseCasesData";

const DEFAULT_SETTINGS = {
  interface: {
    moqToAtom: true,
    entity: false,
    env_prod: true,
    authorize_unpacking: false,
    product_name: {
      caps: false,
    },
    product_variant: {
      display: false,
    },
    product_quantity: {
      moq: true,
      delivery: false,
      atom: false,
    },
    ack_update: false
  },
};

const UseCasesContext = createContext<UseCaseData | undefined>(undefined);

export const useUseCases = (): UseCaseData | undefined => {
  return useContext(UseCasesContext);
};

interface UseCasesProviderProps {
  children: ReactNode;
}

export const UseCasesProvider = ({ children }: UseCasesProviderProps) => {
  const { data } = useUseCasesData();

  const settingsWithDefaults = useMemo(() => {
    if (!data) return { settings: DEFAULT_SETTINGS };

    return {
      ...data,
      settings: mergeWithDefaults(data.settings, DEFAULT_SETTINGS),
    };
  }, [data]);

  return (
    <UseCasesContext.Provider value={settingsWithDefaults}>{children}</UseCasesContext.Provider>
  );
};

const mergeWithDefaults = (data: any, defaults: any): any => {
  if (!data || typeof data !== "object") return defaults;
  if (!defaults || typeof defaults !== "object") return data;

  const result = { ...defaults };

  for (const key in data) {
    if (data.hasOwnProperty(key)) {
      if (data[key] && typeof data[key] === "object" && !Array.isArray(data[key])) {
        result[key] = mergeWithDefaults(data[key], defaults[key] ?? {});
      } else {
        result[key] = data[key];
      }
    }
  }

  return result;
};
